import { createSelector, createSlice } from '@reduxjs/toolkit';

import { DataLayerReservationTypeEnum, LoadingState } from '@nocowanie/core';

import { SliceNameEnum } from '@app/enums';
import { AccommodationStaticStateModel, apiTypes } from '@app/models';
import {
    AccommodationPageModel,
    BreadcrumbsItemModel,
    PointOfInterestModel,
} from '@app/models/elastic-search';
import { getAccommodationStatic } from '@app/state/api-thunks';

type StateType = Omit<AccommodationPageModel, 'breadcrumbs'>;

const initialState: AccommodationStaticStateModel = {
    loading: LoadingState.PENDING,
    data: null,
    linkSpace: null,
    breadcrumbs: null,
    pointOfInterest: null,
    isBestseller: null,
    cityDetails: null,
    ratings: null,
};

const slice = createSlice({
    name: SliceNameEnum.AccommodationStatic,
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder.addCase(getAccommodationStatic.pending, state => {
            state.loading = LoadingState.PENDING;
        });
        builder.addCase(getAccommodationStatic.rejected, (state, action) => {
            state.errors = [action.error].flat();
            state.loading = LoadingState.IDLE;
            state.data = null;
        });
        builder.addCase(getAccommodationStatic.fulfilled, (state, { payload }) => {
            state.errors = null;
            state.loading = LoadingState.IDLE;

            const {
                staticData,
                linkSpace,
                breadcrumbs,
                pointOfInterest,
                isBestseller,
                cityDetails,
                ratings,
            } = payload.data;

            // We save data even it may be null/undefined, why? Because this might mean that request was done
            // for nonexistent (id) accommodation, which doesn't mean that we get error,
            // just that for that particular accommodation there's no data
            state.data = staticData || null;
            state.linkSpace = linkSpace;
            state.breadcrumbs = breadcrumbs ?? null;
            state.pointOfInterest = pointOfInterest ?? null;
            state.isBestseller = isBestseller;
            state.cityDetails = cityDetails ?? null;
            state.ratings = ratings ?? null;
        });
    },
});

const { actions: sliceActions } = slice;
const actions = {
    ...sliceActions,
    getAccommodationStatic,
};

const selectSelfState = (state: {
    [SliceNameEnum.AccommodationStatic]: AccommodationStaticStateModel;
}) => {
    return state[slice.name] ?? initialState;
};

const selectAccommodation = createSelector(selectSelfState, state => state.data);
const selectLinkSpace = createSelector(selectSelfState, state => state.linkSpace);
const selectBreadcrumbs = createSelector(selectSelfState, state => state.breadcrumbs);
const selectPointOfInterest = createSelector(selectSelfState, state => state.pointOfInterest);
const selectIsBestseller = createSelector(selectSelfState, state => state.isBestseller);
const selectCityDetails = createSelector(selectSelfState, state => state.cityDetails);
const selectOperatorInformation = createSelector(selectSelfState, state => state.data?.payer);
// Open api types suggest that breadcrumb may not have name and/or link, but it doesn't make sense so we filter these out.
const selectBreadcrumbsList = createSelector(
    selectBreadcrumbs,
    state =>
        state?.items?.filter(crumb => crumb.name && crumb.item && crumb.position) as
            | Required<BreadcrumbsItemModel>[]
            | undefined,
);
const selectPointOfInterestList = createSelector(
    selectPointOfInterest,
    state =>
        state?.filter(poi => poi.name && poi.distanceInMeters) as
            | Required<PointOfInterestModel>[]
            | null
            | undefined,
);

// graphql indicates that image may be null, which doesn't seem to make sense, so we filter and typecast
const selectImages = (accom: StateType | null) => {
    const accomImages = (accom?.images
        ? accom.images.filter(image => !!image)
        : []) as unknown as apiTypes.Image[];
    const apartmentImages = accom?.apartments
        ? accom.apartments.flatMap(apartment => {
              return (apartment?.images
                  ? apartment.images.filter(image => !!image)
                  : []) as unknown as apiTypes.Image[];
          })
        : [];

    return [...accomImages, ...apartmentImages];
};

// filtered out potential nulls from arrays
const selectInternalAmenityGroups = (accom: StateType | null) => {
    return (
        accom?.features.internalAmenityGroups
            ? accom.features.internalAmenityGroups.filter(group => !!group)
            : null
    ) as apiTypes.Maybe<apiTypes.InternalAmenityGroup[]>;
};

// filtered out potential nulls from arrays
const selectServices = (accom: StateType | null) => {
    return (
        accom?.features.services
            ? accom.features.services.filter(service => !!service && service.name !== 'Unknown')
            : null
    ) as apiTypes.Maybe<apiTypes.Service[]>;
};

const selectors = {
    selectAccommodation,
    selectLoading: createSelector(selectSelfState, state => state.loading),
    selectErrors: createSelector(selectSelfState, state => state.errors),
    selectIsDataLoaded: createSelector(selectAccommodation, state => !!state),
    selectGalleryData: createSelector(selectAccommodation, state => ({
        images: selectImages(state),
        name: state?.name,
    })),
    selectBasicInformationData: createSelector(selectAccommodation, state => ({
        name: state?.name,
        address: state?.location.address,
        streetNumber: state?.location.streetNumber,
        postalCode: state?.location.postalCode,
        settlementName: state?.location.settlement?.name,
        ratingAverage: state?.rating.ratingsAverage ?? state?.rating.ratingAverage,
        ratingCount: state?.rating.ratingCount,
        ratingDescription: state?.rating.ratingDescription,
        internalLabel: state?.features.internalLabel,
        labels: state?.features.labels,
    })),
    selectRoomData: createSelector(selectAccommodation, state => ({
        roomCount: state?.apartments?.length || 0,
    })),
    selectLocationData: createSelector(selectAccommodation, state => ({
        latitude: state?.location.latitude,
        longitude: state?.location.longitude,
        name: state?.name,
        address: state?.location.address,
        streetNumber: state?.location.streetNumber,
        settlementName: state?.location.settlement?.name,
    })),
    selectMetaTagsData: createSelector(selectAccommodation, state => ({
        name: state?.name,
        mainImg: state?.images?.[0]?.url,
    })),
    selectRichSnippetsData: createSelector(selectAccommodation, state => ({
        type: state?.type,
        name: state?.name,
        description: state?.description,
        numberOfRooms: state?.apartments?.length,
        image: state?.images?.[0]?.url,
        addressCountry: state?.location.country?.code,
        addressLocality: state?.location.settlement?.name,
        addressRegion: state?.location.regions?.[1]?.name,
        postalCode: state?.location.postalCode,
        streetAddress:
            state?.location.address && state?.location.streetNumber
                ? `${state.location.address} ${state.location.streetNumber}`
                : state?.location.address,
        latitude: state?.location.latitude,
        longitude: state?.location.longitude,
        ratingAverage: state?.rating.ratingsAverage ?? state?.rating.ratingAverage,
        ratingCount: state?.rating.ratingCount,
        services: selectServices(state),
        internalAmenityGroups: selectInternalAmenityGroups(state),
        labels: state?.features.labels,
        cheapestRate: state?.stay?.cheapestRate,
        currency: state?.location.currency,
        convertedCurrency: null, // convertedCurrency isn't in ElasticSearch
    })),
    selectAccommodationDataLayerData: createSelector(selectSelfState, state => {
        const { data, cityDetails } = state;

        return {
            internalAmenityGroups: selectInternalAmenityGroups(data),
            labels: data?.features.labels,
            offerDestType: data?.type,
            offerName: data?.name,
            offerLocation: data?.location.settlement?.name,
            offerLocationID: cityDetails?.id,
            offerPhotos: selectImages(data).length,
            offerReservationModel:
                data?.features.internalLabel?.reservationType ===
                apiTypes.ReservationTypeEnum.RealTime
                    ? DataLayerReservationTypeEnum.RealTime
                    : DataLayerReservationTypeEnum.OnRequest,
            ratingCount: data?.rating.ratingCount,
            ratingDescription: data?.rating.ratingDescription,
        };
    }),
    selectAmenitiesData: createSelector(selectAccommodation, state => ({
        internalAmenityGroups: selectInternalAmenityGroups(state),
        services: selectServices(state),
    })),
    selectReviewsData: createSelector(selectAccommodation, state => ({
        ratingAverage: state?.rating.ratingsAverage ?? state?.rating.ratingAverage,
        ratingCount: state?.rating.ratingCount,
        ratingDescription: state?.rating.ratingDescription,
        aggregateRating: state?.rating.aggregateRating,
    })),
    selectRecentReviews: createSelector(selectSelfState, state => state?.ratings?.collection),
    selectSeoLinksData: selectLinkSpace,
    selectBreadcrumbsData: selectBreadcrumbs,
    selectBreadcrumbsListData: selectBreadcrumbsList,
    selectPointOfInterestListData: selectPointOfInterestList,
    selectDescription: createSelector(selectAccommodation, state => ({
        description: state?.description,
    })),
    selectImportantInfoData: createSelector(selectAccommodation, state => ({
        checkInStart: state?.stay.checkInStart,
        checkInEnd: state?.stay.checkInEnd,
        checkOutStart: state?.stay.checkOutStart,
        checkOutEnd: state?.stay.checkOutEnd,
    })),
    selectContactPhoneData: createSelector(selectAccommodation, state => ({
        hotelPhone: state?.location.hotelPhone,
        contactPhoneVisible: state?.location.contactPhoneVisible,
    })),
    selectAccommodationType: createSelector(selectAccommodation, state => state?.type),
    selectApartmentById: createSelector(
        selectAccommodation,
        state => (apartmentId: string | number | null) => {
            if (!apartmentId) {
                return null;
            }

            return (
                (state?.apartments ?? [])
                    .map(({ id, name }) => ({ id, name }))
                    .find(item => item.id?.toString() === apartmentId.toString()) ?? null
            );
        },
    ),
    selectIsBestseller,
    selectOperatorInformation,
    selectCityLocation: createSelector(selectCityDetails, state => state?.location),
};

export { slice, actions, selectors, initialState };
